import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, Check, Info, TrendingUp } from './icons';
import { CountUp, Reveal } from './primitives';
import { viewportOnce } from './lib/motion';
import { landingBacktest } from '../../../api';
import { calcHelpHtml, calcSettings, feeRange, useSiteContent } from './lib/siteContent';
import { makeT } from './lib/siteTexts';
import arrowGrowth from '../../../assets/arrow-growth.svg';
import './Calculator.css';

const PRESETS = [1000, 5000, 10000, 25000, 50000];
const usd = (v, dec = 0) => v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const HOME_CONFIG = (() => {
  // [01.01.2023 .. yesterday] (ТЗ 39 п.8; сторінка /returns лишає 2020),
  // period ≥ 1 year (data downloads run through yesterday's close).
  const max = new Date();
  max.setHours(0, 0, 0, 0);
  max.setDate(max.getDate() - 1);
  const min = new Date(2023, 0, 1);
  let start = new Date(max);
  start.setFullYear(start.getFullYear() - 1);
  if (start < min) start = new Date(min);
  return {
    sectionId: 'calculator',
    // Заголовок і підпис підсумку — з адмінки (Тексти сторінок → home.calc).
    textSection: 'home.calc',
    dateMin: min,
    dateMax: max,
    minPeriodMonths: 12,
    defaultStart: start,
    defaultEnd: max,
  };
})();

/* ---- Date helpers (local dates, no TZ-shift surprises) ---- */
const MONTHS_UK = [
  'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень',
];
const WEEKDAYS_UK = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];
const pad = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtDateShort = (d) => `${d.getDate()}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
function fmtIsoShort(iso) {
  if (!iso) return '';
  const [y, m, day] = String(iso).slice(0, 10).split('-');
  return `${day}.${m}.${y}`;
}

// ── tiny cookie helpers (no extra deps) — mirrors the previous landing calc ─
const COOKIE_KEY = 'landing_calc_v1';
const LS_RESULT_KEY = 'landing_calc_result_v1';
function setCookie(name, value, days = 180) {
  try {
    const exp = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${exp}; path=/; SameSite=Lax`;
  } catch (_) { /* ignore */ }
}
function getCookie(name) {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (_) { return null; }
}

function DatePicker({ value, onChange, ariaLabel, min, max }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => new Date(value.getFullYear(), value.getMonth(), 1));
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const minD = min ? dayStart(min) : null;
  const maxD = max ? dayStart(max) : null;
  const isSel = (d) => value.getFullYear() === year && value.getMonth() === month && value.getDate() === d;
  const isDisabled = (d) => {
    const date = new Date(year, month, d);
    return (!!minD && date < minD) || (!!maxD && date > maxD);
  };

  return (
    <div className="datepicker" ref={wrapRef}>
      <button
        type="button"
        className="calc-input"
        onClick={() => {
          setView(new Date(value.getFullYear(), value.getMonth(), 1));
          setOpen((o) => !o);
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${ariaLabel}: ${fmtDate(value)}`}
      >
        <Calendar size={20} />
        {fmtDate(value)}
      </button>

      {open && (
        <div className="dp-pop" role="dialog">
          <div className="dp-head">
            <button type="button" className="dp-nav" onClick={() => setView(new Date(year, month - 1, 1))} aria-label="Попередній місяць">
              <ChevronLeft size={18} />
            </button>
            <span className="dp-title">
              {MONTHS_UK[month]} {year}
            </span>
            <button type="button" className="dp-nav" onClick={() => setView(new Date(year, month + 1, 1))} aria-label="Наступний місяць">
              <ChevronRight size={18} />
            </button>
          </div>
          <div className="dp-grid dp-week">
            {WEEKDAYS_UK.map((w) => (
              <span key={w} className="dp-wd">
                {w}
              </span>
            ))}
          </div>
          <div className="dp-grid">
            {cells.map((d, i) => (d === null ? (
              <span key={`e${i}`} />
            ) : (
              <button
                key={d}
                type="button"
                className={`dp-day${isSel(d) ? ' selected' : ''}`}
                disabled={isDisabled(d)}
                onClick={() => {
                  onChange(new Date(year, month, d));
                  setOpen(false);
                }}
              >
                {d}
              </button>
            )))}
          </div>
        </div>
      )}
    </div>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="calc-chart-tooltip">
      <div className="calc-chart-tooltip__date">{fmtIsoShort(p.date)}</div>
      <div className="calc-chart-tooltip__value">{usd(p.balance, 2)} $</div>
      {p.balance_gross != null && p.balance_gross !== p.balance && (
        <div className="calc-chart-tooltip__gross">без витрат: {usd(p.balance_gross, 2)} $</div>
      )}
    </div>
  );
}

function fmtAxisMoney(n) {
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${Math.round(n / 1e3)}K`;
  return `${Math.round(n)}`;
}

/* Галочка витрати з підказкою «?» (ТЗ 29). locked — комісію Trading прибрати не можна. */
function CostCheck({ on, onToggle, locked, label, hint, helpHtml }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="calc-cost">
      <div className="calc-cost__row">
        <button type="button" className={`calc-check${locked ? ' calc-check--locked' : ''}`}
          onClick={locked ? undefined : onToggle} aria-pressed={on} aria-disabled={locked || undefined}>
          <span className={`calc-check__box${on ? ' on' : ''}`}>{on && <Check size={12} />}</span>
          <span className="calc-check__label">{label}</span>
          {hint && <span className="calc-check__hint">{hint}</span>}
        </button>
        <button type="button" className={`calc-help-btn${open ? ' open' : ''}`} onClick={() => setOpen((o) => !o)}
          aria-expanded={open} aria-label={`Пояснення: ${label}`}>?</button>
      </div>
      {open && <div className="calc-help" dangerouslySetInnerHTML={{ __html: helpHtml }} />}
    </div>
  );
}

export function Calculator({ config = HOME_CONFIG }) {
  const [amount, setAmount] = useState(10000);
  const [reinvest, setReinvest] = useState(true);
  const [depositFee, setDepositFee] = useState(true);
  const [taxes, setTaxes] = useState(true);
  const content = useSiteContent();
  const fees = calcSettings(content);
  const t = makeT(content, 'common.calc');
  // Заголовок секції: з реєстру текстів (config.textSection) або з config.heading.
  const th = config.textSection ? makeT(content, config.textSection) : null;
  const heading = th ? { title: th.rich('title'), subtitle: th.rich('subtitle') } : config.heading;
  const totalLabel = th ? th('total_label') : config.totalLabel;
  const fmtPct = (v) => Number(v).toLocaleString('uk-UA', { maximumFractionDigits: 2 });
  const [result, setResult] = useState(null);
  const [calcId, setCalcId] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(() => config.defaultStart);
  const [endDate, setEndDate] = useState(() => config.defaultEnd);

  // Номер останнього запиту: відповідь старішого (автозапуск, якого обігнав
  // клік) не має перезаписати свіжішу.
  const reqSeq = useRef(0);

  // Restore inputs + headline from cookie, and the full equity curve from
  // localStorage (only reused if it matches the same computation). Then
  // recalculate automatically: the server always backtests the CURRENT active
  // strategy, so a result saved in the browser may belong to a previous one.
  useEffect(() => {
    const inp = {
      amount: 10000, reinvest: true, depositFee: true, taxes: true,
      startDate: config.defaultStart, endDate: config.defaultEnd,
    };
    try {
      const raw = getCookie(COOKIE_KEY);
      if (!raw) throw new Error('no cookie');
      const s = JSON.parse(raw);
      if (s.amount != null) { inp.amount = s.amount; setAmount(s.amount); }
      if (typeof s.reinvest === 'boolean') { inp.reinvest = s.reinvest; setReinvest(s.reinvest); }
      if (typeof s.depositFee === 'boolean') { inp.depositFee = s.depositFee; setDepositFee(s.depositFee); }
      if (typeof s.taxes === 'boolean') { inp.taxes = s.taxes; setTaxes(s.taxes); }
      // Збережена дата могла бути раніше за нинішню межу (головна: 2020 → 2023).
      if (s.startDate) {
        const d = new Date(s.startDate + 'T00:00:00');
        if (!config.dateMin || d >= dayStart(config.dateMin)) { inp.startDate = d; setStartDate(d); }
      }
      if (s.endDate) {
        const d = new Date(s.endDate + 'T00:00:00');
        // Межа «вчора» рухається щодня — збережений кінець за нею лише обрізаємо.
        const e = config.dateMax && d > dayStart(config.dateMax) ? config.dateMax : d;
        inp.endDate = e;
        setEndDate(e);
      }
      // Старий результат без розкладу витрат (до ТЗ 29) не показуємо — він «брудний».
      if (s.result && s.result.costs) {
        let merged = s.result;
        try {
          const full = JSON.parse(localStorage.getItem(LS_RESULT_KEY) || 'null');
          if (full && full.start_date === s.result.start_date &&
              full.end_date === s.result.end_date &&
              full.starting_capital === s.result.starting_capital &&
              full.reinvest === s.result.reinvest &&
              full.costs?.deposit_fee === s.result.costs?.deposit_fee &&
              full.costs?.taxes === s.result.costs?.taxes) {
            merged = full;
          }
        } catch (_) { /* ignore */ }
        setResult(merged);
      }
    } catch (_) { /* ignore */ }
    calculate(inp, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = (res, inp) => {
    const headline = res ? { ...res, equity_curve: undefined } : null;
    setCookie(COOKIE_KEY, JSON.stringify({
      amount: inp.amount, reinvest: inp.reinvest, depositFee: inp.depositFee, taxes: inp.taxes,
      startDate: toIso(inp.startDate), endDate: toIso(inp.endDate), result: headline,
    }));
    try {
      if (res) localStorage.setItem(LS_RESULT_KEY, JSON.stringify(res));
    } catch (_) { /* ignore */ }
  };

  // auto=true — автозапуск при відкритті сторінки: помилок не показує і не
  // прибирає збережений результат, якщо сервер не відповів.
  const calculate = async (inputs = null, auto = false) => {
    const inp = inputs || { amount, reinvest, depositFee, taxes, startDate, endDate };
    const fail = (msg) => {
      if (auto) return;
      setError(msg);
      setResult(null);
    };
    // Date-range validation (Returns page passes minPeriodMonths/dateMin/dateMax)
    if (config.minPeriodMonths) {
      const s = dayStart(inp.startDate);
      const e = dayStart(inp.endDate);
      const min = config.dateMin ? dayStart(config.dateMin) : null;
      const max = config.dateMax ? dayStart(config.dateMax) : null;
      if (e <= s) {
        fail('Дата завершення має бути пізніше за дату початку.');
        return;
      }
      if (min && s < min) {
        fail(`Початок періоду — не раніше ${fmtDate(config.dateMin)}.`);
        return;
      }
      if (max && e > max) {
        fail('Завершення періоду — не пізніше за вчорашню дату.');
        return;
      }
      const months = (e.getFullYear() - s.getFullYear()) * 12 +
        (e.getMonth() - s.getMonth()) -
        (e.getDate() < s.getDate() ? 1 : 0);
      if (months < config.minPeriodMonths) {
        fail('Оберіть період тривалістю не менше ніж рік');
        return;
      }
    }

    if (!auto) setError(null);
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const { data } = await landingBacktest({
        start_date: toIso(inp.startDate),
        end_date: toIso(inp.endDate),
        amount: Number(inp.amount) || 0,
        reinvest: inp.reinvest,
        deposit_fee: inp.depositFee,
        taxes: inp.taxes,
      });
      if (seq !== reqSeq.current) return;
      setError(null);
      setResult(data);
      setCalcId((n) => n + 1);
      persist(data, inp);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      const msg = e?.response?.data?.detail || 'Не вдалося розрахувати. Спробуйте пізніше.';
      fail(typeof msg === 'string' ? msg : 'Помилка розрахунку');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  };

  const profitPositive = result ? (result.profit ?? 0) >= 0 : true;
  const typeLabel = reinvest ? 'Складний %' : 'Простий %';
  const chartData = result?.equity_curve || [];

  return (
    <section className="section calc" id={config.sectionId}>
      <div className="container">
        {heading && (
          <Reveal className="calc__head" variant="fadeBlur">
            <h2 className="calc__title">{heading.title}</h2>
            <p className="calc__subtitle">{heading.subtitle}</p>
          </Reveal>
        )}

        <div className="calc__grid">
          {/* Params card */}
          <motion.div
            className="calc-card calc-params"
            initial={{ opacity: 0, x: -34 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={viewportOnce}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="calc-params__head">
              <h3>{t('params_title')}</h3>
              <p>{t.rich('params_sub')}</p>
            </div>

            <div className="calc-params__body">
              <div className="calc-dates">
                <div className="calc-field">
                  <span className="calc-label">
                    {t('start_label')}{config.dateMin ? ` (не раніше ніж ${fmtDateShort(config.dateMin)})` : ''}
                  </span>
                  <DatePicker value={startDate} onChange={setStartDate} ariaLabel="Дата початку торгів" min={config.dateMin} max={config.dateMax} />
                </div>
                <div className="calc-field">
                  <span className="calc-label">{t('end_label')}</span>
                  <DatePicker value={endDate} onChange={setEndDate} ariaLabel="Дата завершення торгів" min={config.dateMin} max={config.dateMax} />
                </div>
              </div>

              <div className="calc-field">
                <span className="calc-label">{t('amount_label')}</span>
                <div className="calc-chips">
                  {PRESETS.map((p) => (
                    <button key={p} type="button" className={`calc-chip${amount === p ? ' active' : ''}`} onClick={() => setAmount(p)}>
                      {usd(p)} $
                    </button>
                  ))}
                </div>
                <div className="calc-combo">
                  <input
                    className="calc-combo__input"
                    inputMode="numeric"
                    value={usd(amount)}
                    onChange={(e) => setAmount(Number(e.target.value.replace(/\D/g, '')) || 0)}
                    aria-label="Сума інвестиції"
                  />
                  <span className="calc-combo__suffix">$</span>
                  <ChevronDown size={16} className="calc-combo__chev" />
                </div>
              </div>

              <button type="button" className="calc-check" onClick={() => setReinvest((v) => !v)}>
                <span className={`calc-check__box${reinvest ? ' on' : ''}`}>{reinvest && <Check size={12} />}</span>
                <span className="calc-check__label">{t('reinvest_label')}</span>
                <span className="calc-check__hint">{t('reinvest_hint')}</span>
              </button>

              <div className="calc-costs">
                <CostCheck on={depositFee} onToggle={() => setDepositFee((v) => !v)}
                  label={t('fee_deposit')} hint={`— ${fmtPct(fees.deposit_fee_pct)}% від суми`}
                  helpHtml={calcHelpHtml(content, 'deposit_fee')} />
                <CostCheck on={taxes} onToggle={() => setTaxes((v) => !v)}
                  label={t('fee_taxes')} hint={`— ${fmtPct(fees.tax_pct)}% від прибутку щороку`}
                  helpHtml={calcHelpHtml(content, 'taxes')} />
                <CostCheck on locked
                  label={t('fee_trading')} hint={`— ${feeRange(fees)}% від приросту раз на сезон`}
                  helpHtml={calcHelpHtml(content, 'trading_fee')} />
              </div>

              {error && (
                <p className="calc-error" role="alert">
                  {error}
                </p>
              )}

              <button type="button" className="calc-submit" onClick={() => calculate()} disabled={loading}>
                {loading ? t('button_loading') : t('button')}
              </button>
            </div>
          </motion.div>

          {/* Result card */}
          <motion.div
            className="calc-card calc-result"
            initial={{ opacity: 0, x: 34 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={viewportOnce}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="calc-result__head">
              <h3>{t('result_title')}</h3>
              <span className="calc-result__icon">
                <TrendingUp size={18} />
              </span>
            </div>

            <div className="calc-result__body">
              <div className="calc-result__total">
                <span className="calc-result__muted">{totalLabel}</span>
                <strong className="num">
                  {result ? (
                    <>
                      <CountUp key={`t${calcId}`} to={result.final_balance} decimals={2} locale="en-US" /> $
                    </>
                  ) : ('0.0 $')}
                </strong>
              </div>

              <div className="calc-result__split">
                <div>
                  <span className="calc-result__muted">Прибуток</span>
                  <span className={`calc-result__profit num${profitPositive ? '' : ' calc-result__profit--negative'}`}>
                    <span>
                      {result ? (
                        <>
                          {profitPositive ? '+' : ''}
                          <CountUp key={`p${calcId}`} to={result.profit} decimals={2} locale="en-US" /> $
                        </>
                      ) : ('-.-- $')}
                    </span>
                  </span>
                </div>
                <div>
                  <span className="calc-result__muted">Приріст</span>
                  <span className="calc-result__growth num">
                    <span>
                      {result ? (
                        <>
                          {profitPositive ? '+' : ''}
                          <CountUp key={`g${calcId}`} to={result.total_return_pct} decimals={1} locale="en-US" />%
                        </>
                      ) : ('+-.--%')}
                    </span>
                    <span className="calc-result__growth-badge">
                      <img src={arrowGrowth} alt="" width={12} height={12} />
                    </span>
                  </span>
                </div>
              </div>

              <div className="calc-result__rows">
                <div>
                  <span className="calc-result__muted">Початковий депозит</span>
                  <strong className="num">{usd(amount, 2)} $</strong>
                </div>
                <div>
                  <span className="calc-result__muted">Дохідність</span>
                  <span className="num">{result ? `${result.yearly_return_pct.toFixed(1)}% / рік` : '— / рік'}</span>
                </div>
                <div>
                  <span className="calc-result__muted">Тип</span>
                  <span>{typeLabel}</span>
                </div>
              </div>

              {result?.costs && (
                <div className="calc-result__rows calc-result__costs">
                  <div>
                    <span className="calc-result__muted">Без витрат</span>
                    <span className="num">{usd(result.costs.gross_final_balance, 2)} $</span>
                  </div>
                  {result.costs.deposit_fee && (
                    <div>
                      <span className="calc-result__muted">Комісія на ввід</span>
                      <span className="num">−{usd(result.costs.deposit_fee_usd, 2)} $</span>
                    </div>
                  )}
                  <div>
                    <span className="calc-result__muted">Комісія Trading.com.ua</span>
                    <span className="num">−{usd(result.costs.trading_fee_usd, 2)} $</span>
                  </div>
                  {result.costs.taxes && (
                    <div>
                      <span className="calc-result__muted">Податки</span>
                      <span className="num">−{usd(result.costs.taxes_usd, 2)} $</span>
                    </div>
                  )}
                </div>
              )}

              <div className="calc-result__note">
                <Info size={16} />
                <p>{t.rich('disclaimer')}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Equity curve — real backtest result, slides down out of the form after "Розрахувати" */}
        {result && (
          <div className="calc-history-wrap">
            <motion.div className="calc-history-reveal" initial={{ height: 0 }} animate={{ height: 'auto' }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
              <motion.div initial={{ y: '-100%', opacity: 0.5 }} animate={{ y: '0%', opacity: 1 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
                <div className="calc-card calc-history">
                  <div className="calc-history__row">
                    <div className="calc-history__figures">
                      <div className="calc-history__col">
                        <span className="calc-history__muted">{result.strategy_name || 'Стратегія'}</span>
                        <span className="calc-history__big num">{usd(result.final_balance, 2)}</span>
                      </div>
                      <div className="calc-history__col">
                        <span className="calc-history__muted">Зміна за період</span>
                        <span className={`calc-history__change num${profitPositive ? '' : ' calc-result__profit--negative'}`}>
                          {usd(result.profit, 2)}
                          <span className="calc-history__pct">{profitPositive ? '+' : ''}{result.total_return_pct.toFixed(1)}%</span>
                        </span>
                      </div>
                    </div>
                    <div className="calc-history__col calc-history__col--end">
                      <span className="calc-history__muted">Кінцева вартість</span>
                      <span className="calc-history__final num">{usd(result.final_balance, 2)} $</span>
                    </div>
                  </div>

                  {result.costs && (
                    <div className="calc-chart__legend">
                      <span><i className="calc-chart__swatch" /> Ваші кошти після комісій{result.costs.taxes ? ' і податків' : ''}</span>
                      <span><i className="calc-chart__swatch calc-chart__swatch--gross" /> Без витрат</span>
                    </div>
                  )}
                  <div className="calc-chart">
                    <div className="calc-chart__plot" style={{ height: 300 }}>
                      {chartData.length > 1 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                            <defs>
                              <linearGradient id="calcFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#1c69e3" stopOpacity={0.16} />
                                <stop offset="100%" stopColor="#1c69e3" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                            <XAxis dataKey="date" tick={{ fill: '#6a707e', fontSize: 12 }} tickFormatter={fmtIsoShort} minTickGap={48} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: '#6a707e', fontSize: 12 }} tickFormatter={fmtAxisMoney} width={48} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
                            <Tooltip content={<ChartTooltip />} />
                            {result.costs && (
                              <Area type="monotone" dataKey="balance_gross" stroke="#9ca3af" strokeWidth={1.5}
                                strokeDasharray="4 4" fill="none" name="Без витрат" />
                            )}
                            <Area type="monotone" dataKey="balance" stroke="#1c69e3" strokeWidth={2} fill="url(#calcFill)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="calc-chart__empty">Немає даних для графіка за цей період.</div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
}
